export type PageSnapshot = {
  id: string;
  name: string;
  html: string;
  css: string;
};

function safeId(page: any, idx: number): string {
  return String(page?.get?.('id') ?? page?.id ?? `page_${idx + 1}`);
}

function safeName(page: any, idx: number): string {
  return String(page?.get?.('name') ?? `Page ${idx + 1}`);
}

export function getPageSnapshots(editor: any): PageSnapshot[] {
  if (!editor?.Pages?.getAll) return [];
  const all = editor.Pages.getAll();
  const pages = Array.isArray(all) ? all : all?.models ?? [];

  // Some GrapesJS APIs may temporarily switch selected page as a side-effect.
  // Preserve selection to avoid jumping the UI during save/export.
  const prev = editor.Pages.getSelected?.() ?? null;
  const prevKey = prev ? { id: String(prev?.get?.('id') ?? prev?.id ?? ''), name: String(prev?.get?.('name') ?? '') } : null;

  const restore = () => {
    if (!prevKey || !editor?.Pages?.select) return;
    try {
      const selectedNow = editor.Pages.getSelected?.();
      const nowId = String(selectedNow?.get?.('id') ?? selectedNow?.id ?? '');
      if (nowId && prevKey.id && nowId === prevKey.id) return;
      if (prev) editor.Pages.select(prev);
    } catch {
      // ignore
    }
  };

  try {
    return pages.map((page: any, idx: number) => {
      const id = safeId(page, idx);
      const name = safeName(page, idx);
      const main = typeof page?.getMainComponent === 'function' ? page.getMainComponent() : null;

      // Prefer per-page export without switching selected page.
      let html = '';
      try {
        if (main && typeof editor.getHtml === 'function') {
          html = String(editor.getHtml({ component: main }) ?? '');
        }
      } catch {
        // ignore
      }
      if (!html && main && typeof main.toHTML === 'function') {
        try {
          html = String(main.toHTML() ?? '');
        } catch {
          // ignore
        }
      }
      if (!html && typeof editor.getHtml === 'function') {
        html = String(editor.getHtml() ?? '');
      }

      let css = '';
      try {
        if (main && typeof editor.getCss === 'function') {
          css = String(editor.getCss({ component: main }) ?? '');
        }
      } catch {
        // ignore
      }
      if (!css && typeof editor.getCss === 'function') {
        css = String(editor.getCss() ?? '');
      }

      // If a side effect selected another page, switch back immediately.
      restore();

      return { id, name, html, css };
    });
  } finally {
    restore();
  }
}
